if (!customElements.get('product-form')) {
  class ProductForm extends HTMLElement {
    constructor() {
      super();
      if (this.hasChildNodes()) this.init();
    }

    init() {
      this.form = this.querySelector('.js-product-form');
      if (this.form) {
        this.cartDrawer = document.querySelector('cart-drawer');
        this.submitBtn = this.querySelector('[name="add"]');

        if (theme.settings.afterAtc !== 'no-js') {
          this.form.addEventListener('submit', this.handleSubmit.bind(this));
        }

        let parentWrapper = this.closest('.product-info');
        if (parentWrapper) {
          this.priceContainer = parentWrapper.querySelector('.product-info__price .price__current .js-value');
          this.oldPriceContainer = parentWrapper.querySelector('.product-info__price .price__was .js-value');
          this.priceWrapper = parentWrapper.querySelector('.product-info__price .price');
          if (this.priceContainer && this.querySelector('input[name="quantity"]')) {
            this.quantityInput = this.querySelector('input[name="quantity"]');
            let productId = this.form.querySelector('[name="product-id"]').value;
            const productJson = document.getElementById(`product-custom-json-${productId}`);
            if (productJson) {
              this.product = JSON.parse(productJson.textContent);
              this.calculateTotalPrice();
              this.quantityInput.addEventListener('change', this.calculateTotalPrice.bind(this));
            }
          }
        }
      }
    }

    /**
     * Handles submission of the product form.
     * @param {object} evt - Event object.
     */
    async handleSubmit(evt) {
      if (evt.target.id === 'product-signup_form') return;

      evt.preventDefault();

      if (this.submitBtn.getAttribute('aria-disabled') === 'true') return;

      if (theme.settings.vibrateOnATC && window.navigator.vibrate) {
        window.navigator.vibrate(100);
      }

      this.errorMsg = null;
      this.setErrorMsgState();

      // Disable "Add to Cart" button until submission is complete.
      this.submitBtn.setAttribute('aria-disabled', 'true');
      this.submitBtn.classList.add('is-loading');

      const formData = new FormData(this.form);

      if (theme.settings.localCart?.enabled) {
        try {
          await ProductForm.waitForLocalCart();
          await this.addToLocalCart(formData);
        } catch (error) {
          this.dispatchEvent(new CustomEvent('on:cart:error', {
            bubbles: true,
            detail: { error: this.errorMsg?.textContent }
          }));
        } finally {
          this.submitBtn.classList.add('is-success');
          this.submitBtn.removeAttribute('aria-disabled');
          setTimeout(() => {
            this.submitBtn.classList.remove('is-loading');
            this.submitBtn.classList.remove('is-success');
          }, 1400);
        }
        return;
      }

      let sections = 'cart-icon-bubble';
      if (this.cartDrawer) {
        sections += `,${this.cartDrawer.closest('.shopify-section').id.replace('shopify-section-', '')}`;
      }

      formData.append('sections_url', window.location.pathname);
      formData.append('sections', sections);

      const fetchRequestOpts = {
        method: 'POST',
        headers: {
          Accept: 'application/javascript',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: formData
      };

      try {
        const oldCartResponse = await fetch(`${theme.routes.cart}.js`);
        if (!oldCartResponse.ok) throw new Error(oldCartResponse.status);
        const oldCartData = await oldCartResponse.json();

        const response = await fetch(theme.routes.cartAdd, fetchRequestOpts);
        const data = await response.json();
        let error = typeof data.description === 'string' ? data.description : data.message;
        if (data.errors && typeof data.errors === 'object') {
          error = Object.entries(data.errors).map((item) => item[1].join(', '));
        }

        if (data.status) this.setErrorMsgState(error);

        if (!response.ok) throw new Error(response.status);

        if (theme.settings.afterAtc === 'page') {
          // Allow the tick animation to complete
          setTimeout(() => {
            window.location.href = theme.routes.cart;
          }, 300);
        } else {
          // Update cart icon count.
          ProductForm.updateCartIcon(data);

          // If item was added from Quick Add drawer, show "Added to cart" message.
          const quickAddDrawer = this.closest('quick-add-drawer');
          if (quickAddDrawer) quickAddDrawer.addedToCart();

          setTimeout(() => {
            // Update cart drawer contents.
            if (this.cartDrawer) {
              this.cartDrawer.renderContents(
                data,
                !quickAddDrawer && theme.settings.afterAtc === 'drawer'
              );
            } else if (window.location.pathname === theme.routes.cart) {
              const cartItems = document.querySelector('cart-items');
              if (cartItems) {
                if (cartItems.dataset.empty === 'true') {
                  window.location.reload();
                } else {
                  cartItems.refresh();
                }
              }
            }
          }, 700);
        }

        const newCartResponse = await fetch(`${theme.routes.cart}.js`);
        if (!newCartResponse.ok) throw new Error(newCartResponse.status);
        const newCartData = await newCartResponse.json();
        const itemInOldCart = oldCartData.items.filter(
          (item) => item.variant_id === data.variant_id
        )[0];

        // Check if product was already in the cart
        if (itemInOldCart) {
          this.dispatchEvent(new CustomEvent('on:line-item:change', {
            bubbles: true,
            detail: {
              cart: newCartData,
              variantId: data.variant_id,
              oldQuantity: itemInOldCart.quantity,
              newQuantity: (itemInOldCart.quantity === data.quantity)
                ? itemInOldCart.quantity : data.quantity
            }
          }));
        } else {
          this.dispatchEvent(new CustomEvent('on:cart:add', {
            bubbles: true,
            detail: {
              cart: newCartData,
              variantId: data.variant_id
            }
          }));
        }
      } catch (error) {
        console.log(error); // eslint-disable-line
        this.dispatchEvent(new CustomEvent('on:cart:error', {
          bubbles: true,
          detail: {
            error: this.errorMsg?.textContent
          }
        }));

        if (this.cartDrawer) this.cartDrawer.refresh(true);
      } finally {
        // Re-enable 'Add to Cart' button.
        this.submitBtn.classList.add('is-success');
        this.submitBtn.removeAttribute('aria-disabled');
        setTimeout(() => {
          this.submitBtn.classList.remove('is-loading');
          this.submitBtn.classList.remove('is-success');
        }, 1400);
      }
    }

    /**
     * Resolves once the local cart module has initialised.
     */
    static waitForLocalCart() {
      if (window.LocalCart) return Promise.resolve();
      return new Promise((resolve, reject) => {
        const timeout = setTimeout(() => reject(new Error('local_cart_unavailable')), 5000);
        document.addEventListener('local-cart:ready', () => {
          clearTimeout(timeout);
          resolve();
        }, { once: true });
      });
    }

    /**
     * Adds the submitted variant to the local cart instead of Shopify's cart.
     * @param {FormData} formData - Product form data.
     */
    async addToLocalCart(formData) {
      const strings = theme.strings.localCart.atc;
      try {
        const result = await window.LocalCart.addToCart(formData);
        if (result.adjustments.length) {
          this.setErrorMsgState(strings.adjusted.replace('[quantity]', result.newQuantity));
        }

        if (theme.settings.afterAtc === 'page') {
          setTimeout(() => {
            window.location.href = theme.routes.cart;
          }, 300);
        } else {
          const quickAddDrawer = this.closest('quick-add-drawer');
          if (quickAddDrawer) {
            quickAddDrawer.addedToCart();
          } else {
            window.LocalCart.showToast(strings.added, { actionLabel: strings.view_cart, actionHref: theme.routes.cart });
          }
        }

        const detail = { cart: window.LocalCart.store.getState(), variantId: Number(result.line.variantId) };
        if (result.isNew) {
          this.dispatchEvent(new CustomEvent('on:cart:add', { bubbles: true, detail }));
        } else {
          this.dispatchEvent(new CustomEvent('on:line-item:change', {
            bubbles: true,
            detail: { ...detail, oldQuantity: result.oldQuantity, newQuantity: result.newQuantity }
          }));
        }
      } catch (error) {
        const template = strings[error.code] || theme.strings.cartError;
        const details = error.details || {};
        this.setErrorMsgState(template.replace('[quantity]', details.quantity ?? '').replace('[increment]', details.increment ?? ''));
        throw error;
      }
    }

    /**
     * Updates the cart icon count in the header.
     * @param {object} response - Response JSON.
     */
    static updateCartIcon(response) {
      const cartIconBubble = document.getElementById('cart-icon-bubble');
      if (cartIconBubble) {
        cartIconBubble.innerHTML = response.sections['cart-icon-bubble'];
      }
    }

    /**
     * Shows/hides an error message.
     * @param {string} [error=false] - Error to show a message for.
     */
    setErrorMsgState(error = false) {
      this.errorMsg = this.errorMsg || this.querySelector('.js-form-error');
      if (!this.errorMsg) return;

      this.errorMsg.hidden = !error;
      if (error) {
        this.errorMsg.innerHTML = '';
        const errorArray = Array.isArray(error) ? error : [error];
        errorArray.forEach((err, index) => {
          if (index > 0) this.errorMsg.insertAdjacentHTML('beforeend', '<br>');
          this.errorMsg.insertAdjacentText('beforeend', err);
        });
      }
    }

    calculateTotalPrice() {
      let qty = this.form.querySelector('input[name="quantity"]').value;
      let vId = this.form.querySelector('[name="id"]').value;
      let variant = this.product.variants.find(variant => variant.id.toString() === vId.toString());
      if (variant) {
        let unitPrice = variant.price;
        let quantityPriceBreaks = variant.quantity_price_breaks;
        let priceBreak = quantityPriceBreaks.find(priceBreak => priceBreak.minimum_quantity <= qty);
        let hasTierDiscount = priceBreak && priceBreak.price < variant.price;
        if (priceBreak) {
          unitPrice = priceBreak.price;
        }
        let totalPrice = unitPrice * qty;
        this.priceContainer.textContent = Shopify.formatMoney(totalPrice);

        if (this.oldPriceContainer) {
          let oldPrice = variant.compare_at_price || (hasTierDiscount ? variant.price : null);
          if (oldPrice && oldPrice > unitPrice) {
            this.oldPriceContainer.textContent = Shopify.formatMoney(oldPrice * qty);
            if (this.priceWrapper) this.priceWrapper.classList.add('price--on-sale');
          } else {
            this.oldPriceContainer.textContent = '';
            if (this.priceWrapper) this.priceWrapper.classList.remove('price--on-sale');
          }
        }
      }
    }
  }

  customElements.define('product-form', ProductForm);
}
